import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { readingRoutesApi, type ReadingRoute, type RouteMeter, type RouteDevice } from '../api/readingRoutes'
import { readingsApi, type Meter } from '../api/readings'
import { scadaApi, type ScadaDevice } from '../api/scada'
import { usersApi, type User } from '../api/users'
import { useAuthStore } from '../store/authStore'
import { Badge } from '../components/Badge'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// ─── Route form (create / edit) ───────────────────────────────────────────────

interface RouteFormState {
  name: string
  description: string
  assigned_to: string
  route_type: 'reading' | 'repair'
}

function RouteForm({
  initial,
  readers,
  onSave,
  onCancel,
  saving,
  error,
  lockType,
}: {
  initial: RouteFormState
  readers: User[]
  onSave: (v: RouteFormState) => void
  onCancel: () => void
  saving: boolean
  error: string
  lockType?: boolean
}) {
  const [v, setV] = useState<RouteFormState>(initial)

  return (
    <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
      {!lockType && (
        <div className="flex rounded-lg overflow-hidden border border-gray-300 bg-white">
          {(['reading', 'repair'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setV((s) => ({ ...s, route_type: t }))}
              className={`flex-1 py-2 text-xs font-medium transition-colors ${
                v.route_type === t
                  ? t === 'reading' ? 'bg-brand-cyan text-brand-midnight' : 'bg-orange-500 text-white'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              {t === 'reading' ? 'Ruta za očitavanje' : 'Servisni reon'}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Naziv *</label>
          <input
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-hidden bg-white"
            value={v.name}
            onChange={(e) => setV((s) => ({ ...s, name: e.target.value }))}
            placeholder={v.route_type === 'reading' ? 'npr. Ruta 1 — Centar' : 'npr. Reon Sever'}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {v.route_type === 'reading' ? 'Odgovorni inkasant' : 'Odgovorni serviser'}
          </label>
          <select
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-hidden bg-white"
            value={v.assigned_to}
            onChange={(e) => setV((s) => ({ ...s, assigned_to: e.target.value }))}
          >
            <option value="">— niko —</option>
            {readers.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.full_name ?? u.email ?? u.user_id}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Opis</label>
        <input
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-hidden bg-white"
          value={v.description}
          onChange={(e) => setV((s) => ({ ...s, description: e.target.value }))}
          placeholder="Kratki opis oblasti / napomena"
        />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
        >
          Otkaži
        </button>
        <button
          type="button"
          onClick={() => onSave(v)}
          disabled={saving || !v.name.trim()}
          className="px-4 py-2 text-sm font-medium text-brand-midnight bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-50 rounded-lg transition-colors"
        >
          {saving ? 'Čuvam…' : 'Sačuvaj'}
        </button>
      </div>
    </div>
  )
}

// ─── Route detail panel ───────────────────────────────────────────────────────

function RouteDetailPanel({
  route,
  readers,
  onClose,
  isAdmin,
}: {
  route: ReadingRoute
  readers: User[]
  onClose: () => void
  isAdmin: boolean
}) {
  const qc = useQueryClient()
  const [tab, setTab] = useState<'meters' | 'devices'>('meters')

  // — Meters state —
  const [addingMeter, setAddingMeter] = useState(false)
  const [meterSearch, setMeterSearch] = useState('')
  const [selectedMeterId, setSelectedMeterId] = useState('')

  // — SCADA devices state —
  const [addingDevice, setAddingDevice] = useState(false)
  const [selectedDeviceId, setSelectedDeviceId] = useState('')
  const [deviceError, setDeviceError] = useState('')

  const { data: detail, isLoading } = useQuery({
    queryKey: ['route-detail', route.id],
    queryFn: () => readingRoutesApi.get(route.id).then((r) => r.data),
  })

  const { data: allMeters = [] } = useQuery({
    queryKey: ['meters-search', meterSearch],
    queryFn: () => readingsApi.getMeters({ search: meterSearch || undefined, active: true, limit: 50 }).then((r) => r.data),
    enabled: addingMeter,
  })

  const { data: allScadaDevices = [], isLoading: isScadaLoading } = useQuery({
    queryKey: ['scada-devices'],
    queryFn: () => scadaApi.listDevices().then((r) => r.data),
    enabled: addingDevice,
  })

  const removeMeterMut = useMutation({
    mutationFn: (meterId: string) => readingRoutesApi.removeMeter(route.id, meterId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['route-detail', route.id] }),
  })

  const addMeterMut = useMutation({
    mutationFn: ({ meterId, position }: { meterId: string; position: number }) =>
      readingRoutesApi.addMeter(route.id, meterId, position),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['route-detail', route.id] })
      qc.invalidateQueries({ queryKey: ['routes'] })
      setAddingMeter(false)
      setSelectedMeterId('')
      setMeterSearch('')
    },
  })

  const removeDeviceMut = useMutation({
    mutationFn: (deviceId: string) => readingRoutesApi.removeDevice(route.id, deviceId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['route-detail', route.id] }),
  })

  const addDeviceMut = useMutation({
    mutationFn: (deviceId: string) => readingRoutesApi.addDevice(route.id, deviceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['route-detail', route.id] })
      setAddingDevice(false)
      setSelectedDeviceId('')
      setDeviceError('')
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setDeviceError(msg ?? 'Greška pri dodavanju uređaja')
    },
  })

  const assignedReader = readers.find((u) => u.user_id === route.assigned_to)
  const existingMeterIds = new Set(detail?.meters.map((m) => m.id) ?? [])
  const availableMeters = allMeters.filter((m) => !existingMeterIds.has(m.id))
  const existingDeviceIds = new Set(detail?.devices.map((d) => d.id) ?? [])
  const availableDevices = allScadaDevices.filter((d: ScadaDevice) => !existingDeviceIds.has(d.id))

  function handleAddMeter() {
    if (!selectedMeterId) return
    const nextPos = (detail?.meters.length ?? 0) + 1
    addMeterMut.mutate({ meterId: selectedMeterId, position: nextPos })
  }

  const DEVICE_TYPE_LABEL: Record<string, string> = {
    pump:             'Crpna stanica',
    reservoir:        'Rezervoar',
    flow_meter:       'Merač protoka',
    pressure_sensor:  'Senzor pritiska',
    level_sensor:     'Senzor nivoa',
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header — converged onto Drawer.tsx's chrome (Phase 2's deferred bullet,
          unblocked now that Phase 4 built the drawer convention): items-start/
          px-6 py-5/border-gray-100, and the same close-button styling, since
          RouteDetailPanel itself stays a split-pane panel, not an actual Drawer. */}
      <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-900 truncate">{route.name}</h2>
          {route.description && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{route.description}</p>
          )}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 -mr-1 shrink-0" aria-label="Zatvori">
          <svg className="w-5 h-5" viewBox="0 -960 960 960" fill="currentColor">
            <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
          </svg>
        </button>
      </div>

      {/* Meta */}
      <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap gap-4 text-xs text-gray-600">
        <span>
          <span className="font-medium">{route.route_type === 'repair' ? 'Serviser:' : 'Inkasant:'}</span>{' '}
          {assignedReader ? (assignedReader.full_name ?? assignedReader.email) : <em className="text-gray-400">nije dodeljen</em>}
        </span>
        <span>
          <span className="font-medium">Vodomeri:</span> {route.meter_count}
        </span>
        <span>
          <span className="font-medium">SCADA:</span> {detail?.devices.length ?? 0}
        </span>
        <span className={`font-medium ${route.active ? 'text-emerald-600' : 'text-red-500'}`}>
          {route.active ? 'Aktivna' : 'Neaktivna'}
        </span>
      </div>

      {/* Tabs */}
      <div className="flex items-center border-b border-gray-200 bg-white px-5">
        {([['meters', 'Vodomeri'], ['devices', 'SCADA uređaji']] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === key
                ? 'border-brand-cyan text-brand-cyan-text'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
        <div className="flex-1" />
        {isAdmin && tab === 'meters' && !addingMeter && (
          <button
            onClick={() => setAddingMeter(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-brand-cyan-text border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Dodaj vodomer
          </button>
        )}
        {isAdmin && tab === 'devices' && !addingDevice && (
          <button
            onClick={() => setAddingDevice(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-brand-poi-text border border-sky-200 rounded-lg hover:bg-sky-50 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Dodaj uređaj
          </button>
        )}
      </div>

      {/* ── Meters tab ── */}
      {tab === 'meters' && (
        <>
          <div className="flex-1 overflow-y-auto p-5 space-y-2">
            {isLoading && (
              <p className="text-sm text-gray-400 text-center py-6">Učitavanje…</p>
            )}

            {!isLoading && detail?.meters.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-6">
                Nema vodomera u ovoj ruti.{isAdmin && ' Dodajte ih ispod.'}
              </p>
            )}

            {detail?.meters.map((m: RouteMeter, idx: number) => (
              <div
                key={m.id}
                className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors"
              >
                <span className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 text-xs font-bold text-gray-500">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{m.subscriber_name}</p>
                  <p className="text-xs text-gray-500 truncate">{m.address}</p>
                  {m.last_reading_date && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Poslednje očitanje: {m.last_reading_value?.toFixed(3)} m³ — {fmtDate(m.last_reading_date)}
                    </p>
                  )}
                </div>
                <span className="shrink-0 text-xs font-mono text-gray-400">{m.meter_serial}</span>
                {isAdmin && (
                  <button
                    onClick={() => removeMeterMut.mutate(m.id)}
                    disabled={removeMeterMut.isPending}
                    className="shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Ukloni iz ture"
                  >
                    <svg className="w-4 h-4" viewBox="0 -960 960 960" fill="currentColor">
                      <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          {isAdmin && (
            <div className="border-t border-gray-200 p-4">
              {addingMeter && (
                <div className="space-y-2">
                  <input
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-hidden"
                    placeholder="Pretraži vodomere…"
                    value={meterSearch}
                    onChange={(e) => { setMeterSearch(e.target.value); setSelectedMeterId('') }}
                    autoFocus
                  />
                  {availableMeters.length > 0 && (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto bg-white">
                      {availableMeters.map((m: Meter) => (
                        <button
                          key={m.id}
                          onClick={() => setSelectedMeterId(m.id)}
                          className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors ${selectedMeterId === m.id ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`}
                        >
                          <p className="text-sm font-medium text-gray-900">{m.subscriber_name}</p>
                          <p className="text-xs text-gray-500">{m.address} · {m.meter_serial}</p>
                        </button>
                      ))}
                    </div>
                  )}
                  {meterSearch && availableMeters.length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-2">Nema rezultata</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setAddingMeter(false); setSelectedMeterId(''); setMeterSearch('') }}
                      className="flex-1 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      Otkaži
                    </button>
                    <button
                      onClick={handleAddMeter}
                      disabled={!selectedMeterId || addMeterMut.isPending}
                      className="flex-1 py-2 text-sm font-medium text-brand-midnight bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-50 rounded-lg transition-colors"
                    >
                      {addMeterMut.isPending ? 'Dodajem…' : 'Dodaj'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── SCADA devices tab ── */}
      {tab === 'devices' && (
        <>
          <div className="flex-1 overflow-y-auto p-5 space-y-2">
            {isLoading && (
              <p className="text-sm text-gray-400 text-center py-6">Učitavanje…</p>
            )}

            {!isLoading && detail?.devices.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-6">
                Nema SCADA uređaja u ovoj turi.{isAdmin && ' Dodajte ih ispod.'}
              </p>
            )}

            {detail?.devices.map((d: RouteDevice, idx: number) => (
              <div
                key={d.id}
                className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors"
              >
                <span className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-sky-50 text-xs font-bold text-brand-poi-text">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{d.label}</p>
                  <p className="text-xs text-gray-500 font-mono">{d.device_key}</p>
                </div>
                <Badge className={`shrink-0 ${
                  d.node_type === 'pump'
                    ? 'bg-blue-100 text-brand-cyan-text'
                    : 'bg-teal-100 text-teal-700'
                }`}>
                  {DEVICE_TYPE_LABEL[d.node_type] ?? d.node_type}
                </Badge>
                {!d.active && (
                  <span className="shrink-0 px-1.5 py-0.5 rounded-sm text-xs bg-red-100 text-red-500 font-medium">
                    Neaktivan
                  </span>
                )}
                {isAdmin && (
                  <button
                    onClick={() => removeDeviceMut.mutate(d.id)}
                    disabled={removeDeviceMut.isPending}
                    className="shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Ukloni iz ture"
                  >
                    <svg className="w-4 h-4" viewBox="0 -960 960 960" fill="currentColor">
                      <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          {isAdmin && (
            <div className="border-t border-gray-200 p-4">
              {addingDevice && (
                <div className="space-y-2">
                  {deviceError && (
                    <p className="text-xs text-red-600 text-center py-1">{deviceError}</p>
                  )}
                  {isScadaLoading ? (
                    <p className="text-xs text-gray-400 text-center py-2">Učitavanje uređaja…</p>
                  ) : availableDevices.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-2">
                      {allScadaDevices.length === 0
                        ? 'Nema konfigurisanih SCADA uređaja'
                        : 'Svi SCADA uređaji su već dodati'}
                    </p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto bg-white">
                      {availableDevices.map((d: ScadaDevice) => (
                        <button
                          key={d.id}
                          onClick={() => setSelectedDeviceId(d.id)}
                          className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors ${selectedDeviceId === d.id ? 'bg-sky-50 border-l-2 border-brand-poi' : ''}`}
                        >
                          <p className="text-sm font-medium text-gray-900">{d.label}</p>
                          <p className="text-xs text-gray-500">{DEVICE_TYPE_LABEL[d.node_type]} · {d.device_key}</p>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setAddingDevice(false); setSelectedDeviceId(''); setDeviceError('') }}
                      className="flex-1 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      Otkaži
                    </button>
                    <button
                      onClick={() => selectedDeviceId && addDeviceMut.mutate(selectedDeviceId)}
                      disabled={!selectedDeviceId || addDeviceMut.isPending}
                      className="flex-1 py-2 text-sm font-medium text-brand-midnight bg-brand-poi hover:opacity-90 disabled:opacity-50 rounded-lg transition-colors"
                    >
                      {addDeviceMut.isPending ? 'Dodajem…' : 'Dodaj'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function RoutesPage() {
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const isAdmin = user?.roles.some((r) => ['admin', 'company_admin'].includes(r)) ?? false

  const [selected, setSelected] = useState<ReadingRoute | null>(null)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<ReadingRoute | null>(null)
  const [formError, setFormError] = useState('')
  const [showInactive, setShowInactive] = useState(false)
  const [typeFilter, setTypeFilter] = useState<'all' | 'reading' | 'repair'>('all')

  const { data: routes = [], isLoading } = useQuery({
    queryKey: ['routes', showInactive, typeFilter],
    queryFn: () => readingRoutesApi.list({
      active_only: !showInactive,
      route_type: typeFilter === 'all' ? undefined : typeFilter,
    }).then((r) => r.data),
  })

  const { data: allUsers = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list().then((r) => r.data),
  })

  const readers = allUsers.filter((u) => u.roles.includes('meter_reader'))

  const createMut = useMutation({
    mutationFn: (v: RouteFormState) => readingRoutesApi.create({
      name: v.name.trim(),
      description: v.description.trim() || undefined,
      assigned_to: v.assigned_to || undefined,
      route_type: v.route_type,
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['routes'] })
      setCreating(false)
      setFormError('')
      setSelected(res.data)
    },
    onError: () => setFormError('Greška pri čuvanju'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, v }: { id: string; v: RouteFormState }) => readingRoutesApi.update(id, {
      name: v.name.trim(),
      // null, not undefined -- PATCH now reads an omitted key as "leave
      // alone," so an emptied field has to actually say so to clear.
      description: v.description.trim() || null,
      assigned_to: v.assigned_to || null,
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['routes'] })
      setEditing(null)
      setFormError('')
      if (selected?.id === res.data.id) setSelected(res.data)
    },
    onError: () => setFormError('Greška pri čuvanju'),
  })

  const toggleActive = useMutation({
    mutationFn: (r: ReadingRoute) => readingRoutesApi.update(r.id, { active: !r.active }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['routes'] })
      if (selected?.id === res.data.id) setSelected(res.data)
    },
  })

  const readerName = (id: string | null) => {
    if (!id) return null
    const u = allUsers.find((u) => u.user_id === id)
    return u?.full_name ?? u?.email ?? null
  }

  return (
    <div className="flex h-full min-h-screen bg-gray-50">
      {/* Left panel — route list */}
      <div className={`flex flex-col ${selected ? 'hidden lg:flex lg:w-96' : 'flex-1 lg:w-96 lg:flex-none'} border-r border-gray-200 bg-white`}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 space-y-3">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold text-gray-900">Rute i reoni</h1>
            {isAdmin && !creating && (
              <button
                onClick={() => { setCreating(true); setEditing(null) }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-midnight bg-brand-cyan hover:bg-brand-cyan-dark rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                {typeFilter === 'repair' ? 'Novi reon' : 'Nova ruta'}
              </button>
            )}
          </div>

          {/* Create form */}
          {creating && (
            <RouteForm
              initial={{ name: '', description: '', assigned_to: '', route_type: typeFilter === 'repair' ? 'repair' : 'reading' }}
              readers={readers}
              onSave={(v) => createMut.mutate(v)}
              onCancel={() => { setCreating(false); setFormError('') }}
              saving={createMut.isPending}
              error={formError}
            />
          )}

          <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
            {([['all', 'Sve'], ['reading', 'Rute'], ['repair', 'Reoni']] as const).map(([val, label]) => (
              <button
                key={val}
                onClick={() => { setTypeFilter(val); setSelected(null) }}
                className={`flex-1 px-3 py-1.5 font-medium transition-colors ${
                  typeFilter === val
                    ? val === 'repair' ? 'bg-orange-500 text-white' : 'bg-brand-cyan text-brand-midnight'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded-sm"
            />
            Prikaži neaktivne
          </label>
        </div>

        {/* Route cards */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {isLoading && (
            <p className="text-sm text-gray-400 text-center py-8">Učitavanje…</p>
          )}

          {!isLoading && routes.length === 0 && (
            <div className="text-center py-10 text-gray-400">
              <svg className="w-10 h-10 mx-auto mb-3 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              <p className="text-sm">Nema ruta/reona. {isAdmin && 'Kliknite "Nova ruta" ili "Novi reon".'}</p>
            </div>
          )}

          {routes.map((r) => {
            const isSelected = selected?.id === r.id

            if (editing?.id === r.id) {
              return (
                <div key={r.id} className="border border-gray-200 rounded-xl p-3">
                  <RouteForm
                    initial={{ name: r.name, description: r.description ?? '', assigned_to: r.assigned_to ?? '', route_type: r.route_type }}
                    readers={readers}
                    onSave={(v) => updateMut.mutate({ id: r.id, v })}
                    onCancel={() => { setEditing(null); setFormError('') }}
                    saving={updateMut.isPending}
                    error={formError}
                    lockType
                  />
                </div>
              )
            }

            return (
              <div
                key={r.id}
                onClick={() => setSelected(r)}
                className={`p-4 rounded-xl border cursor-pointer transition-all ${
                  isSelected
                    ? 'border-blue-400 bg-blue-50 shadow-xs'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-xs'
                } ${!r.active ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-900 truncate">{r.name}</p>
                  <div className="flex shrink-0 gap-1">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      r.route_type === 'repair'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-blue-100 text-brand-cyan-text'
                    }`}>
                      {r.route_type === 'repair' ? 'Reon' : 'Ruta'}
                    </span>
                    {!r.active && (
                      <span className="px-1.5 py-0.5 rounded-sm text-xs bg-red-100 text-red-600 font-medium">
                        Neaktivna
                      </span>
                    )}
                  </div>
                </div>
                {r.description && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{r.description}</p>
                )}
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {r.meter_count} vodom.
                  </span>
                  {r.assigned_to && (
                    <span className="flex items-center gap-1 truncate">
                      <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="truncate">{readerName(r.assigned_to) ?? '—'}</span>
                    </span>
                  )}
                </div>

                {isAdmin && (
                  <div
                    className="flex gap-2 mt-3 pt-2.5 border-t border-gray-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => { setEditing(r); setSelected(null) }}
                      className="flex-1 text-xs font-medium text-gray-600 hover:text-brand-cyan-text py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      Uredi
                    </button>
                    <button
                      onClick={() => toggleActive.mutate(r)}
                      disabled={toggleActive.isPending}
                      className={`flex-1 text-xs font-medium py-1.5 rounded-lg transition-colors disabled:opacity-50 ${r.active ? 'text-red-500 hover:text-red-700 hover:bg-red-50' : 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'}`}
                    >
                      {r.active ? 'Deaktiviraj' : 'Aktiviraj'}
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Right panel — route detail */}
      {selected ? (
        <div className="flex-1 bg-white flex flex-col">
          <RouteDetailPanel
            route={selected}
            readers={readers}
            onClose={() => setSelected(null)}
            isAdmin={isAdmin}
          />
        </div>
      ) : (
        <div className="hidden lg:flex flex-1 items-center justify-center text-gray-400">
          <div className="text-center">
            <svg className="w-14 h-14 mx-auto mb-4 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            <p className="text-sm">Izaberite turu da vidite vodomere</p>
          </div>
        </div>
      )}
    </div>
  )
}
